import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { requestPasswordReset } from '@/lib/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-pd py-16 max-w-md" data-testid="forgot-password-page">
      <div className="font-mono text-[10px] text-[color:var(--pd-gold)] uppercase tracking-[0.2em]">Password reset</div>
      <h1 className="font-display text-5xl leading-none mt-3">Forgot your password?</h1>
      <p className="text-muted-sage mt-3 text-sm">
        Enter the email on your Padel Draw account and we&rsquo;ll send you a link to choose a new password. The link expires after 60 minutes and can only be used once.
      </p>

      {sent ? (
        <div
          className="mt-8 rounded-2xl border border-[color:var(--pd-gold)]/40 bg-surface p-6"
          data-testid="forgot-password-sent"
        >
          <div className="font-display text-2xl">Check your inbox.</div>
          <p className="text-sm text-muted-sage mt-2">
            If an account exists for <span className="text-[color:var(--pd-text)]">{email}</span>, a reset link is on its way. It can take a minute or two to arrive &mdash; also worth checking spam.
          </p>
          <p className="text-xs text-muted-sage mt-3">
            Didn&rsquo;t receive it after a few minutes? You can{' '}
            <button
              type="button"
              className="underline text-[color:var(--pd-accent-yellow)]"
              onClick={() => { setSent(false); }}
              data-testid="forgot-password-resend"
            >
              try again
            </button>{' '}
            or contact support.
          </p>
          <div className="mt-5">
            <Link to="/login" className="text-sm text-[color:var(--pd-accent-yellow)] underline">
              &larr; Back to sign in
            </Link>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="forgot-password-form">
          <input
            required
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-12 rounded-xl bg-surface border border-pd px-4 text-sm focus:border-[color:var(--pd-accent-yellow)] focus:outline-none"
            data-testid="forgot-password-email"
          />
          <button
            disabled={loading}
            className="w-full bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-xl px-4 py-3.5 font-medium disabled:opacity-60"
            data-testid="forgot-password-submit"
          >
            {loading ? 'Sending…' : 'Send reset link'}
          </button>
          <div className="pt-2 text-center">
            <Link to="/login" className="text-sm text-muted-sage hover:text-[color:var(--pd-accent-yellow)] underline underline-offset-4">
              Remembered it? Sign in
            </Link>
          </div>
        </form>
      )}
    </div>
  );
};

export default ForgotPasswordPage;
